import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ProcessViewModel } from "../../../models/Process";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { DateTime } from "luxon";
import { crm } from "../../../theme";

const FinancialTable = ({
  onSort,
  sortField,
  setSortField,
  sortDirection,
  setSortDirection,
  data,
  onOpenDetail,
  setSelectedItem,
  currency,
}: {
  onSort: (field: string, direction: "asc" | "desc") => void;
  sortField?: string | undefined;
  setSortField?: Dispatch<SetStateAction<string | undefined>>;
  sortDirection?: "asc" | "desc";
  setSortDirection?: Dispatch<SetStateAction<"asc" | "desc">>;
  data: any[];
  currency?: string;
  onOpenDetail: () => void;
  setSelectedItem: Dispatch<SetStateAction<ProcessViewModel>>;
}) => {
  const formatterBrl = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const renderSortIcon = (field: string) => {
    if (sortField !== field) return <FaSort />;
    return sortDirection === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const handleHeaderClick = (field: string) => {
    if (sortField === field) {
      const newDirection = sortDirection === "asc" ? "desc" : "asc";

      onSort(sortDirection === "desc" ? "" : field, newDirection);
      setSortDirection && setSortDirection(newDirection);
    } else {
      setSortField && setSortField(field);
      setSortDirection && setSortDirection("asc");
      onSort(field, "asc");
    }
  };

  const columns = [
    {
      label: "Nº do processo",
      sortable: true,
      index: "cvfRef",
      data: (p: any) => (
        <Flex alignItems="center" gap="16px">
          {p.cvfRef}
        </Flex>
      ),
    },
    {
      label: "Termo de pagamento",
      width: "300px",
      data: (p: any) => (
        <Flex alignItems="center" gap="16px">
          {p.condition}
        </Flex>
      ),
    },
    // {
    //   label: "Total pago",
    //   sortable: true,
    //   index: "totalPayed",
    //   data: (p: any) => (
    //     <Flex alignItems="center" gap="16px">
    //       color="green.600" fontWeight="normal">
    //         {formatterUsd.format(parseFloat(p.totalPayed))}
    //
    //     </Flex>
    //   ),
    // },
    // {
    //   label: "Total a pagar",
    //   sortable: true,
    //   index: "totalToPay",
    //   data: (p: any) => (
    //     <Flex alignItems="center" gap="16px">
    //       color="red.600" fontWeight="normal">
    //         {formatterUsd.format(parseFloat(p.totalToPay))}
    //
    //     </Flex>
    //   ),
    // },
    {
      label: "Total do processo",
      data: (p: any) => (
        <Flex alignItems="center" gap="16px">
          {p.totalInvoice}
        </Flex>
      ),
    },
    {
      label: "Total do processo",
      data: (p: any) => (
        <Flex alignItems="center" gap="16px">
          {p.totalInvoice}
        </Flex>
      ),
    },
  ];
  const [hasFirst, setHasFirst] = useState(false);
  const [hasSecond, setHasSecond] = useState(false);
  const [hasDesembaraco, setHasDesembaraco] = useState(false);
  useEffect(() => {
    setHasFirst(data.some((d) => d.financialItems[0]));
    setHasSecond(data.some((d) => d.financialItems[1]));
    setHasDesembaraco(data.some((d) => d.dataPagamentoDesembaraco));
  }, [data]);

  const totalColumns =
    columns.length +
    (hasDesembaraco ? 2 : 0) +
    (hasFirst ? 4 : 0) +
    (hasSecond ? 4 : 0) +
    1;

  const scrollRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 53,
    overscan: 12,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - virtualRows[virtualRows.length - 1].end
      : 0;

  return (
    <Box ref={scrollRef} height="60vh" overflowY="auto" overflowX="auto">
      <Table>
        <Thead bg={crm.subtle}>
          <Tr>
            {columns.map((c, idx) => (
              <Th
                key={c.index ?? c.label + idx}
                onClick={() =>
                  c.sortable ? handleHeaderClick(c.index ?? "") : null
                }
                cursor={c.sortable ? "pointer" : undefined}
                w={c.width}
                bg={crm.subtle}
                position={"sticky"}
                right={c.label.length === 0 ? 0 : undefined}
                top={0}
                zIndex={10}
                textTransform="none"
                fontWeight="700"
                color={crm.textMuted}
                borderColor={crm.border}
              >
                <Flex alignItems="center" gap="8px">
                  {c.label}
                  {c.sortable && <Box>{renderSortIcon(c.index ?? "")}</Box>}
                </Flex>
              </Th>
            ))}
            {hasDesembaraco && (
              <>
                <Th
                  bg={crm.subtle}
                  position={"sticky"}
                  top={0}
                  zIndex={10}
                  whiteSpace="pre-line"
                  w="300px"
                  textAlign="center"
                  textTransform="none"
                  fontWeight="700"
                  color={crm.textMuted}
                  borderColor={crm.border}
                >
                  Data de pagamento do desembaraço
                </Th>
                <Th
                  bg={crm.subtle}
                  position={"sticky"}
                  top={0}
                  zIndex={10}
                  textTransform="none"
                  fontWeight="700"
                  color={crm.textMuted}
                  borderColor={crm.border}
                >
                  Valor desembaraço
                </Th>
              </>
            )}
            {hasFirst && (
              <>
                <Th
                  bg={crm.subtle}
                  position={"sticky"}
                  top={0}
                  zIndex={10}
                  textTransform="none"
                  fontWeight="700"
                  color={crm.textMuted}
                  borderColor={crm.border}
                >
                  Sinal de pagamento
                </Th>
                <Th
                  bg={crm.subtle}
                  position={"sticky"}
                  top={0}
                  zIndex={10}
                  textTransform="none"
                  fontWeight="700"
                  color={crm.textMuted}
                  borderColor={crm.border}
                >
                  Data de pagamento
                </Th>
                <Th
                  bg={crm.subtle}
                  position={"sticky"}
                  top={0}
                  zIndex={10}
                  textTransform="none"
                  fontWeight="700"
                  color={crm.textMuted}
                  borderColor={crm.border}
                >
                  Taxa de câmbio Sinal
                </Th>
                <Th
                  bg={crm.subtle}
                  position={"sticky"}
                  top={0}
                  zIndex={10}
                  textTransform="none"
                  fontWeight="700"
                  color={crm.textMuted}
                  borderColor={crm.border}
                >
                  Contrato de câmbio
                </Th>
              </>
            )}

            {hasSecond && (
              <>
                <Th
                  bg={crm.subtle}
                  position={"sticky"}
                  top={0}
                  zIndex={10}
                  textTransform="none"
                  fontWeight="700"
                  color={crm.textMuted}
                  borderColor={crm.border}
                >
                  Saldo de pagamento
                </Th>
                <Th
                  bg={crm.subtle}
                  position={"sticky"}
                  top={0}
                  zIndex={10}
                  textTransform="none"
                  fontWeight="700"
                  color={crm.textMuted}
                  borderColor={crm.border}
                >
                  Data de pagamento
                </Th>
                <Th
                  bg={crm.subtle}
                  position={"sticky"}
                  top={0}
                  zIndex={10}
                  textTransform="none"
                  fontWeight="700"
                  color={crm.textMuted}
                  borderColor={crm.border}
                >
                  Taxa de câmbio Saldo
                </Th>

                <Th
                  bg={crm.subtle}
                  position={"sticky"}
                  top={0}
                  zIndex={10}
                  textTransform="none"
                  fontWeight="700"
                  color={crm.textMuted}
                  borderColor={crm.border}
                >
                  Contrato de câmbio
                </Th>
              </>
            )}
            <Th
              bg={crm.subtle}
              position={"sticky"}
              top={0}
              zIndex={10}
              right={0}
              textTransform="none"
              fontWeight="700"
              color={crm.textMuted}
              borderColor={crm.border}
            ></Th>
          </Tr>
        </Thead>
        <Tbody>
          {paddingTop > 0 && (
            <Tr>
              <Td
                colSpan={totalColumns}
                style={{ height: paddingTop, padding: 0, border: 0 }}
              />
            </Tr>
          )}
          {virtualRows.map((virtualRow: any) => {
            const d = data[virtualRow.index];
            return (
            <Tr
              key={d.id ?? virtualRow.index}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
            >
              {columns.map((c, colIdx) => (
                <Td key={c.index ?? c.label + colIdx} w={c.width} backgroundColor="white" whiteSpace="pre-line">
                  {c.data(d)}
                </Td>
              ))}

              {hasDesembaraco && (
                <>
                  <Td backgroundColor="white" whiteSpace="pre-line">
                    {d.dataPagamentoDesembaraco &&
                      DateTime.fromISO(d.dataPagamentoDesembaraco).toFormat(
                        "dd/LL/yyyy",
                      )}
                  </Td>
                  <Td backgroundColor="white" whiteSpace="pre-line">
                    {d.dataPagamentoDesembaraco && d.valorDesembaraco > 0
                      ? formatterBrl.format(d.valorDesembaraco)
                      : ""}
                  </Td>
                </>
              )}
              {hasFirst && (
                <>
                  <Td backgroundColor="white" whiteSpace="pre-line">
                    {d.financialItems[0].valorPagamento}
                  </Td>
                  <Td backgroundColor="white" whiteSpace="pre-line">
                    {d.financialItems[0].dataPagamento &&
                      DateTime.fromISO(
                        d.financialItems[0].dataPagamento,
                      ).toFormat("dd/LL/yyyy")}
                  </Td>
                  <Td backgroundColor="white" whiteSpace="pre-line">
                    {d.financialItems[0].taxaCambio}
                  </Td>
                  <Td backgroundColor="white" whiteSpace="pre-line">
                    {d.financialItems[0].contratoCambio}
                  </Td>
                </>
              )}

              {hasSecond && (
                <>
                  <Td backgroundColor="white" whiteSpace="pre-line">
                    {d.financialItems[1]?.valorPagamento}
                  </Td>
                  <Td backgroundColor="white" whiteSpace="pre-line">
                    {d.financialItems[1]?.dataPagamento &&
                      DateTime.fromISO(
                        d.financialItems[1]?.dataPagamento,
                      ).toFormat("dd/LL/yyyy")}
                  </Td>
                  <Td backgroundColor="white" whiteSpace="pre-line">
                    {d.financialItems[1]?.taxaCambio}
                  </Td>
                  <Td backgroundColor="white" whiteSpace="pre-line">
                    {d.financialItems[1]?.contratoCambio}
                  </Td>
                </>
              )}
              <Td
                backgroundColor="white"
                position="sticky"
                right={0}
                whiteSpace="pre-line"
              >
                <Flex
                  alignItems="center"
                  gap="16px"
                  cursor="pointer"
                  color={crm.brand}
                  borderRadius="8px"
                  p="4px"
                  _hover={{ bg: crm.brandSoft }}
                  onClick={() => {
                    setSelectedItem(d);
                    onOpenDetail();
                  }}
                >
                  <span className="material-symbols-outlined">article</span>
                </Flex>
              </Td>
            </Tr>
            );
          })}
          {paddingBottom > 0 && (
            <Tr>
              <Td
                colSpan={totalColumns}
                style={{ height: paddingBottom, padding: 0, border: 0 }}
              />
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
};

export default FinancialTable;
